/**
 * WordPress dependencies.
 */
import { __ } from '@wordpress/i18n';
import { RichText, useBlockProps } from '@wordpress/block-editor';
import {
	Fragment
} from '@wordpress/element'; 
import classnames from 'classnames';
import { useEffect} from '@wordpress/element';
import { useSelect } from '@wordpress/data';
/**
 * Internal dependencies
 */
 import Controls from './controls.js'; 
 import InsSettings from './settings.js';
 import './editor.scss';
 import useDynamicContent from '../../dynamic-content/useDynamicContent';
 import useDynamicContentLoop from '../../dynamic-content/useDynamicContentLoop';
 import { VayuBlocksDevice } from '@vayu-blocks/device-control';
 import { VayuBlocksUniqueId, VayuBlocksTypography,VayuBlocksCssTextAlign,getResponsiveRangeValue } from "@vayu-blocks/global";
 import { VayuBlockAdvanceStyle } from '@vayu-blocks/advance-panel/style.js';

 export default function Edit(props) {
	
	const {attributes, setAttributes, clientId, isSelected, context } = props;

	const { mergedStyles, handleMouseEnter, handleMouseLeave, hoverAnimationClass } = VayuBlockAdvanceStyle(attributes);
	
	const { id } = attributes;

	if ( ! id ) {
		  setAttributes( { id: clientId } );
	}
	  
	VayuBlocksUniqueId({ 
				setAttributes: setAttributes, 
				clientId: clientId
	});

	const deviceView = VayuBlocksDevice();

    let stylesheet;

	stylesheet = {
		'--heading-color':attributes.headingColor,
        '--heading-hvr-color':attributes.headingHvrColor,
	}

	let backgroundStyle = {};

	if ('color' === attributes.backgroundType) {
		backgroundStyle = {
		  ...backgroundStyle,
		  '--h-background': attributes.backgroundColor,
		};
	  }
	  
	  if ('color' === attributes.backgroundTypeHvr) {
		backgroundStyle = {
		  ...backgroundStyle,
		  '--h-background-hover': attributes.backgroundColorHvr,
		};
	  }
	  
	  if ('image' === attributes.backgroundType) {
		backgroundStyle = {
		  ...backgroundStyle,
		  '--h-background': attributes.backgroundImage?.url ? `url('${attributes.backgroundImage.url}')` : 'none',
		  '--h-background-attachment': attributes.backgroundAttachment,
		  '--h-background-position': `${Math.round(attributes.backgroundPosition?.x * 100)}% ${Math.round(attributes.backgroundPosition?.y * 100)}%`,
		  '--h-background-repeat': attributes.backgroundRepeat,
		  '--h-background-size': attributes.backgroundSize,
		};
	  }
	  
	  if ('image' === attributes.backgroundTypeHvr) {
		backgroundStyle = {
		  ...backgroundStyle,
		  '--h-background-hover': attributes.backgroundImageHvr?.url ? `url('${attributes.backgroundImageHvr.url}')` : 'none',
		  '--h-background-attachment-hvr': attributes.backgroundAttachmentHvr,
		  '--h-background-position-hvr': `${Math.round(attributes.backgroundPositionHvr?.x * 100)}% ${Math.round(attributes.backgroundPositionHvr?.y * 100)}%`,
		  '--h-background-repeat-hvr': attributes.backgroundRepeatHvr,
		  '--h-background-size-hvr': attributes.backgroundSizeHvr,
		};
	  }
	  
	  if ('gradient' === attributes.backgroundType) {
		backgroundStyle = {
		  ...backgroundStyle,
		  '--h-background': attributes.backgroundGradient,
		};
	  }
	  
	  if ('gradient' === attributes.backgroundTypeHvr) {
		backgroundStyle = {
		  ...backgroundStyle,
		  '--h-background-hover': attributes.backgroundGradientHvr,
		};
	  }

	  // Final style merge
	  const style = {
		  ...mergedStyles,
		  ...stylesheet,
		  ...backgroundStyle,
		  ...VayuBlocksCssTextAlign(attributes.align,deviceView),
		  ...VayuBlocksTypography(attributes.typography, deviceView),
		  backgroundImage: attributes.headingimage ? `url(${attributes.headingimage})` : undefined,
	  };

	let headingWidthClass = "";

	if(attributes.advWidth?.value =='fullwidth') {
		headingWidthClass = 'alignfull';
	}
	if( attributes.advWidth?.value =='customwidth') {
		headingWidthClass = 'alignwide';
	}

  const Classes = classnames(
  hoverAnimationClass || null, // Ensure it's a valid string or null
  headingWidthClass || null,   // Likewise, avoid adding undefined
  attributes.headingimage && attributes.headinganimation && 'vayu_blocks_heading_image_animation-heading',
  attributes.headingimage && 'vayu_blocks_heading_image-heading',
  {
    'th-hide-desktop': attributes.advResponsive?.Desktop === true,
    'th-hide-tablet': attributes.advResponsive?.Tablet === true,
    'th-hide-mobile': attributes.advResponsive?.Mobile === true,
  }
);

    const blockProps = useBlockProps({
		id:`th-block-heading-${attributes.uniqueId}`,
		className:Classes,
		style
	});

	const headingBlockProps = {
		...blockProps,
		onMouseEnter: handleMouseEnter,
		onMouseLeave: handleMouseLeave,
	};

	const { postId, postType, queryId } = context;
	const isDescendentOfQueryLoop = Number.isFinite( queryId );

	// Fetch content using DynamicContentFetcher
	const contentToSet = useDynamicContentLoop({
		postId,
		postType,
		selectedSourceField: attributes.selectedSourceField,
		attributes
	});

	if ( isDescendentOfQueryLoop) {
		// Update content when postId or postTitle changes, but only if no custom content is set
		useEffect(() => {
			if (postId && contentToSet ) {
				// Only set content if it's not already manually set by the user
				setAttributes({ content: contentToSet });
			}
		}, [contentToSet, attributes.content]);
	}

	const dynamicContent = useDynamicContent(attributes);

    useEffect(() => {
			if (dynamicContent !== null && dynamicContent !== '') {
				setAttributes({ content: dynamicContent });
			}
	}, [dynamicContent, setAttributes]);
	
	
	const changeContent = (value) => {
		setAttributes({ content: value });
	};

	return (
        <Fragment>
           {isSelected && <Controls 
				attributes={ attributes }
				setAttributes={ setAttributes }
			/>}
           {isSelected && <InsSettings
				{...props}
			/>}
					<RichText
						identifier="content"
						tagName={attributes.tag} // The tag used for content output in the editor
						value={attributes.content} // Value stored in the block attribute
						allowedFormats={['core/bold', 'core/italic', 'core/link']} // Allowed formatting options
						onChange={changeContent} // Update content on change
						placeholder={__('Write heading…', 'vayu-blocks')} // Placeholder text before user input
						{...headingBlockProps}
					/>
        </Fragment>
	);
}
